import { makeStyles } from '@material-ui/core'
import { Handle, Position } from '@xyflow/react'
import clsx from 'clsx'
import React, { useCallback, useMemo, useState } from 'react'
import { nodePropTypes } from '../../../Canvas/nodes/constants'
import { ICON_NAMES } from '../../../../../../constants'
import Icon from '../../../../../atoms/Icon'
import { useGetNodeValues } from '../../../Canvas/nodes/hooks/useGetNodeValues'
import { useEntityGraphContext } from '../../../Wrapper/EntityGraphProvider'
import { usePublicImageUrl } from '../../../../../../hooks/usePublicResource'
import SydModal from '../../../../../commonDesign/SydModal'
import { useCheckPolicy } from '../../../../../../hooks'
import EditPersonForm from './editForms/EditPersonForm'

const useStyles = makeStyles(() => ({
  container: ({ canSelect }) => ({
    position: 'relative',
    cursor: canSelect ? 'pointer' : 'default',
    '& .__edit-toggle, & .__details-toggle': {
      opacity: 0,
      position: 'absolute',
      background: 'var(--color-primary-400)',
      color: 'var(--color-contrast-primary-400)',
      borderRadius: 'var(--border-radius-round)',
      zIndex: 10,
      width: '2em',
      height: '2em',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 0.3s',
      cursor: 'pointer',
      border: '2px solid white',
      '&:hover': {
        background: 'var(--color-primary-500)'
      }
    },
    '& .__edit-toggle': {
      top: '-0.5em',
      right: '-0.5em'
    },
    '& .__details-toggle': {
      top: '-0.5em',
      left: '-0.5em'
    },
    '&:hover .__edit-toggle, &:hover .__details-toggle': {
      opacity: 1
    }
  }),
  node: ({ size }) => ({
    border: '2px solid var(--color-neutral-300)',
    boxShadow: 'var(--shadow-md)',
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: 'var(--border-radius-lg)',
    padding: '10px',
    minWidth: size === 'lg' ? '200px' : size === 'sm' ? '120px' : '160px',
    fontSize: size === 'lg' ? '1rem' : size === 'sm' ? '0.5em' : '0.7em',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5em',
    '&.selected': {
      outline: '2px solid var(--color-primary-400)',
      outlineOffset: '2px'
    }
  }),
  imageContainer: ({ size }) => ({
    width: size === 'sm' ? '2em' : size === 'lg' ? '4em' : '3em',
    height: size === 'sm' ? '2em' : size === 'lg' ? '4em' : '3em',
    borderRadius: '100%',
    overflow: 'hidden',
    border: '2px solid var(--color-neutral-300)',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }
  }),
  placeholderImage: ({ size }) => ({
    width: size === 'sm' ? '2em' : size === 'lg' ? '4em' : '3em',
    height: size === 'sm' ? '2em' : size === 'lg' ? '4em' : '3em',
    borderRadius: '100%',
    background: 'var(--color-neutral-200)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500)',
    border: '2px solid var(--color-neutral-300)'
  }),
  name: {
    fontWeight: 600,
    fontSize: '1em',
    textAlign: 'center',
    margin: 0
  },
  details: {
    fontSize: '0.85em',
    textAlign: 'center',
    color: 'var(--color-neutral-600)',
    margin: 0
  }
}))

const handlePositions = [
  { id: 'top', style: { opacity: 0, top: 0, left: '50%' }, position: Position.Top },
  { id: 'right', style: { opacity: 0, top: '50%', right: 0 }, position: Position.Right },
  { id: 'bottom', style: { opacity: 0, top: '100%', left: '50%' }, position: Position.Bottom },
  { id: 'left', style: { opacity: 0, top: '50%', left: 0 }, position: Position.Left }
]

export default function FamilyTreeNode ({ id, data, selected }) {
  const size = data?.nodeProps?.size || 'md'
  const { state, dispatch } = useEntityGraphContext()

  const userHasEditPermissions = useCheckPolicy('admin_edit_persons')
  const canEditNode = useMemo(() => state.config.canvasSettings.nodesEditable, [state.config.canvasSettings.nodesEditable])
  const canEditContent = useMemo(() => state.config.familyTree?.canEditContent && userHasEditPermissions, [state.config.familyTree?.canEditContent, userHasEditPermissions])
  const canSeeDetails = useMemo(() => state.config.canvasSettings.canSeeDetails, [state.config.canvasSettings.canSeeDetails])

  const classes = useStyles({ size, canSelect: canEditNode })

  const {
    header,
    description,
    image: _image,
    backgroundColor,
    textColor,
    borderColor
  } = useGetNodeValues(data, state.linkedEntityData)

  const imagePublicUrl = usePublicImageUrl(_image)
  const image = useMemo(() => _image?.length && !_image.startsWith('http') ? imagePublicUrl : _image, [_image, imagePublicUrl])

  const [isEditing, setIsEditing] = useState(false)
  const onSubmitChange = (updatedData) => {
    setIsEditing(false)
    dispatch({
      type: 'UPDATE_NODE',
      payload: {
        nodeId: id,
        path: ['data', 'entityProps'],
        value: {
          ...data.entityProps,
          ...updatedData
        }
      }
    })
  }

  const onViewDetails = useCallback(() => {
    dispatch({
      type: 'TOGGLE_EDIT_SIDEBAR',
      payload: {
        path: 'detailsItem',
        value: {
          id,
          type: 'node'
        }
      }
    })
  }, [dispatch, id])

  const style = useMemo(() => ({
    backgroundColor,
    color: textColor,
    borderColor
  }), [backgroundColor, textColor, borderColor])

  return (
    <>
      {canEditContent && (
        <SydModal
          open={isEditing}
          onClose={() => setIsEditing(false)}
          title={`Edit "${header}"`}
          maxWidth='md'
          fullWidth
          showCloseButton
          disablePortal={false}
        >
          <EditPersonForm
            defaultValues={{
              ...data.entityProps,
              chartLevel: {
                levelId: state.levelId,
                levelTypeId: state.levelTypeId
              }
            }}
            onSubmit={onSubmitChange}
            onCancel={() => setIsEditing(false)}
          />
        </SydModal>
      )}
      <div className={clsx(classes.container, { selected: selected && canEditNode })}>
        {canEditContent && (
          <button onClick={() => setIsEditing(!isEditing)} className='__edit-toggle'>
            <Icon name={ICON_NAMES.edit} customSize='10px' />
          </button>
        )}
        {canSeeDetails && (
          <button onClick={onViewDetails} className='__details-toggle'>
            <Icon name={ICON_NAMES.info} customSize='10px' />
          </button>
        )}

        {handlePositions.map(({ position, style, id }) => (
          <Handle key={id} id={`handle_${id}`} type='source' position={position} style={style ?? {}} />
        ))}
        {handlePositions.map(({ position, style, id }) => (
          <Handle key={id} id={`target_${id}`} type='target' position={position} style={style ?? {}} />
        ))}

        <div className={clsx(classes.node, { selected: selected && canEditNode })} style={style}>
          {image ? (
            <div className={classes.imageContainer}>
              <img src={image} alt={header} />
            </div>
          ) : (
            <div className={classes.placeholderImage}>
              <Icon name={ICON_NAMES.user} customSize={size === 'lg' ? 24 : size === 'sm' ? 8 : 10} />
            </div>
          )}
          <p className={classes.name}>{header}</p>
          {description && <p className={classes.details}>{description}</p>}
        </div>
      </div>
    </>
  )
}

FamilyTreeNode.propTypes = nodePropTypes
